"use client";

import { ArrowRight, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Invitation() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const processNotes = [{
    number: "01",
    label: "Reservation"
  }, {
    number: "02",
    label: "Confirmation"
  }, {
    number: "03",
    label: "Arrival"
  }];
  return <section data-section-id="1488" id="the-invitation" className="relative overflow-hidden py-24 md:py-32 bg-background text-foreground">
      {}
      <div aria-hidden="true" className="absolute top-0 left-0 right-0 flex items-center">
        <span className="inline-block size-1.5 rounded-lg ml-6 md:ml-12 bg-silver-leaf" />
        <span className="flex-1 h-px bg-asphalt-steel" />
        <span className="inline-block size-1.5 rounded-lg mr-6 md:mr-12 bg-silver-leaf" />
      </div>

      {}
      <svg aria-hidden="true" className="pointer-events-none absolute -top-10 -right-10 w-72 h-72 opacity-10" viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="currentColor" strokeWidth="1" strokeLinecap="round">
        <path d="M180 20 C 140 60, 110 100, 60 140" />
        <path d="M180 20 C 160 30, 145 50, 130 55" />
        <path d="M180 20 C 170 50, 165 75, 150 85" />
        <path d="M180 20 C 145 35, 120 60, 110 85" />
        <path d="M180 20 C 130 50, 100 90, 90 120" />
        <path d="M180 20 C 120 40, 80 80, 70 130" />
      </svg>

      {}
      <svg aria-hidden="true" className="pointer-events-none absolute -bottom-10 -left-10 w-72 h-72 opacity-10 rotate-180" viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="currentColor" strokeWidth="1" strokeLinecap="round">
        <path d="M180 20 C 140 60, 110 100, 60 140" />
        <path d="M180 20 C 160 30, 145 50, 130 55" />
        <path d="M180 20 C 170 50, 165 75, 150 85" />
        <path d="M180 20 C 145 35, 120 60, 110 85" />
        <path d="M180 20 C 130 50, 100 90, 90 120" />
        <path d="M180 20 C 120 40, 80 80, 70 130" />
      </svg>

      <div className="relative mx-auto max-w-[1320px] px-6 md:px-12">
        <div ref={ref} className={`grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          {}
          <div className="lg:col-span-8 order-2 lg:order-1">
            {}
            <div className="font-mono text-xs font-medium uppercase mb-8 flex items-center gap-2 text-mercury-accent tracking-[0.2em]">
              <span>§ 01 // RESERVATION</span>
            </div>

            {}
            <h2 className="font-default font-medium mb-8 text-platinum-bright" style={{
            fontSize: "clamp(2rem, 5vw, 3.5rem)",
            letterSpacing: "-0.02em",
            lineHeight: "1.1"
          }}>
              Your private transit,{" "}
              <span className="font-default italic text-silver-leaf">
                refined.
              </span>
            </h2>

            {}
            <p className="max-w-2xl mb-10 text-silver-leaf font-light" style={{
            fontSize: "clamp(1.125rem, 1.5vw, 1.25rem)",
            lineHeight: "1.6"
          }}>
              BEMAX LIMO CHAUFFEURED SERVICE provides an elite, seamless experience for those who demand punctuality and discretion. Secure your vehicle today and experience the standard of private travel.
            </p>

            {}
            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              <Button asChild className="rounded-lg h-14 px-10 font-default font-semibold text-sm group bg-platinum-bright text-midnight-void hover:bg-silver-leaf transition-colors duration-300">
                <Link to="https://book.mylimobiz.com/v4/bemax" newTab>
                  <span>Reserve Now</span>
                  <ArrowRight className="size-4 transition-transform duration-200 group-hover:translate-x-1" />
                </Link>
              </Button>

              <Button asChild className="bg-transparent border border-mercury-accent text-silver-leaf hover:border-platinum-bright hover:text-platinum-bright rounded-lg h-14 px-10 font-default font-semibold text-sm transition-colors duration-300">
                <Link to="https://wa.me/13109419596?text=I%20would%20like%20to%20book%20a%20chauffeured%20service" newTab>
                  <MessageCircle className="size-4" />
                  <span>WhatsApp</span>
                </Link>
              </Button>

              <Button asChild variant="outline" className="rounded-lg h-14 px-10 font-default font-semibold text-sm bg-transparent border border-mercury-accent text-silver-leaf hover:border-platinum-bright hover:text-platinum-bright transition-colors duration-300">
                <Link to="/FleetPage">View Our Fleet</Link>
              </Button>
            </div>

            {}
            <div className="flex flex-col md:flex-row md:items-stretch gap-4 md:gap-0">
              {processNotes.map((note, i) => <div data-index={i} key={note.number} className="flex md:flex-1 items-center gap-4">
                  <div className="flex items-baseline gap-3 flex-1">
                    <span className="font-mono text-xs font-medium text-silver-leaf tracking-[0.1em]">
                      {note.number}
                    </span>
                    <span className="font-default text-base md:text-lg text-platinum-bright">
                      {note.label}
                    </span>
                  </div>
                  {i < processNotes.length - 1 && <div aria-hidden="true" className="hidden md:flex items-center px-4">
                      <span className="inline-block w-8 h-px bg-asphalt-steel" />
                    </div>}
                </div>)}
            </div>
          </div>

          {}
          <div className="lg:col-span-4 order-1 lg:order-2 relative">
            <div className="relative mx-auto max-w-md lg:max-w-none">
              {}
              <div aria-hidden="true" className="absolute -right-12 lg:-right-20 top-1/2 -translate-y-1/2 size-[280px] md:size-[360px] rounded-lg pointer-events-none bg-asphalt-steel" />

              {}
              <div className="relative aspect-[4/5] overflow-hidden rounded-lg border border-asphalt-steel">
                <Link className="w-full h-full object-cover grayscale-[20%] underline cursor-pointer" to="/FleetPage"><img alt="Professional chauffeur standing by a luxury black sedan at a private terminal" loading="lazy" decoding="async" className="w-full h-full object-cover grayscale-[20%]" src="http://bemaxlimo.com/wp-content/uploads/2026/09/IMG_4058-2.webp" /></Link>
              </div>

              {}
              <div className="absolute -top-4 -left-4 px-4 py-2 rounded-lg border border-asphalt-steel bg-jet-surface">
                <span className="font-default italic text-sm text-silver-leaf">
                  Punctuality
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}